import { AppBar, Grid, Toolbar, useTheme } from "@mui/material";
import {
	BackdropCircularProgress,
	EmptyList,
	EnhancedSnackbar,
} from "@websential/cosmic-v20";
import {
	EMPLOYEE_LIST_SCREEN_SLUG,
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../../common/constants";
import { FormProvider, useForm } from "react-hook-form";
import { IResponse, IServerError } from "../../../models/common";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {

	getDDLLabelByValue,
	sortDDLByLabel,
	useNavigateToLogout,
	useRemoveQueriesOnUnmount,
} from "../../../common/Common";
import {
	setSelectedActivityLogActions,
	setSelectedStickyNavBar,
} from "../../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { EmployeeService } from "../../../services/EmployeeService";
import { IAppState } from "../../../store/reducers";
import { IDropDownListResponse } from "../../../models/common/IDropDownListResponse";
import { IGetListRequest } from "../../../models";
import { ReportsService } from "../../../services/ReportsService";
import { setSelectedDrawerOptionsMenu } from "../../../store/actions/drawerMenuOptionsActions";
import { useNavigate } from "react-router-dom";
import { ROEReportEarningDetailDataGridWithColumnToggled } from "./ROEReportEarningDetailDataGridWithColumnToggled";
import { ROEReportHeader } from "./ROEReportHeader";
import FileSaver from "file-saver";
import { ROEReportHoursDataGridWithColumnToggled } from "./ROEReportHoursDataGridWithColumnToggled";

export interface IROEReportSetupPage { }
const ROE_REPORT_QUERY_KEY = "roe-report";


const useROEReport = (
	runGetListAPICall: boolean,
	clientId: string,
	employee: string,
	params: IGetListRequest,
	navigateToLogout: (path: any) => void
) =>
	useQuery({
		queryKey: [
			ROE_REPORT_QUERY_KEY,
			runGetListAPICall,
			clientId,
			employee,
			params,
		],
		queryFn: () =>
			ReportsService.getROEReport(
				runGetListAPICall,
				clientId,
				employee,
				params,
				navigateToLogout
			),
		refetchOnWindowFocus: false,
	});

const useGetROEEmployeeDDLWithDate = (clientId: string) =>
	useQuery({
		queryKey: ["roe-employee-ddl-with-date", clientId],
		queryFn: () => {
			return ReportsService.getROEDDLWithDate(clientId);
		},
		refetchOnWindowFocus: false,
	});

export const ROEReport: React.FC<IROEReportSetupPage> = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const clientIdStore = useSelector((state: IAppState) => state.client.id);
	const clientName = useSelector((state: IAppState) => state.client.clientName);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const navigateToLogout = useNavigateToLogout();

	const queryClient = useQueryClient();
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [isLoading, setIsLoading] = useState(false);
	const [roeReportData, setROEReportData] =
		useState<any>(undefined);
	const [runGetListAPICall, setRunGetListAPICall] =
		useState<any>(true);


	const formMethods = useForm<any>({
		mode: "onBlur",
		reValidateMode: "onChange",
	});

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "name",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const {isPending:isROEEmployeeDDLLoading,  data: roeEmployeeDDLWithDate } = useGetROEEmployeeDDLWithDate(clientId);

	const employee = formMethods.getValues("employee");

	const { isPending, data } = useROEReport(
		runGetListAPICall,
		clientIdStore,
		employee && employee,
		queryParams,
		navigateToLogout
	);
	const roeReportList: any | null = data;
	useEffect(() => {
		if (roeReportList && roeReportList.roe_report_hours_data) {
			setROEReportData(roeReportList);
		}
	}, [roeReportList]);

	//  after click apply button api calls with updated values and update roe report data
	const roeReportDataAPICall = (
		employee: string,
	) => {
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const response: any = await ReportsService.getROEReport(
					true,
					clientIdStore,
					employee,
					queryParams,
					navigateToLogout
				);

				if (response) {
					setROEReportData(response);
				}
			} catch {
			} finally {
				setIsLoading(false);
			}
		};
		fetchData();
	};

	const onSubmit = (values: any) => {
		roeReportDataAPICall(
			values.employee,
		);
	
	};

	const [zeroEmployee, setzeroEmployee] = useState(true);

	const [submitAddRemoveColumnsHandler, setSubmitAddRemoveColumnsHandler] =
		useState(undefined);
	const [onColumnReorder, setOnColumnReorder] = useState(undefined);

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "name",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
		setRunGetListAPICall(true)
	}, []);

	const {
		isPending: isSubmittingAddRemoveHeaderColumns,
		mutate: submitAddRemoveHeaderColumns,
	} = useMutation({
		mutationFn: ReportsService.getROEAddRemoveHeaderColumns,
		onSuccess: (res: any | IServerError | IResponse) => {
			if (res.status === 200) {
				setRunGetListAPICall(true)
				return queryClient.invalidateQueries({
					queryKey: [ROE_REPORT_QUERY_KEY],
				});
			} else {
				setSnackbarData({
					open: true,
					message: res.message
						? res.message
						: "Something went wrong, try again later!",
					severity: "error",
				});
			}
		},
		onError: (err: any) => {
			setSnackbarData({
				open: true,
				message: err.message,
				severity: "error",
			});
		},
	});


	useEffect(() => {
		if (submitAddRemoveColumnsHandler) {
			submitAddRemoveHeaderColumns({
				clientId: clientId,
				columns: submitAddRemoveColumnsHandler,
				navigateToLogout: navigateToLogout,
			});
		}
	}, [submitAddRemoveColumnsHandler]);

	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	useRemoveQueriesOnUnmount(["employee-report"]);

	const updatedROEHoursDetailsListItems = useMemo(() => {
		return (
			roeReportData &&
			roeReportData?.roe_report_hours_data.items &&
			roeReportData?.roe_report_hours_data.items.map((item: any) => ({
				...item,
			}))
		);
	}, [roeReportData]);

	const updatedROEEarningDetailsListItems = useMemo(() => {
		return (
			roeReportData &&
			roeReportData?.roe_report_earnings_data.items &&
			roeReportData?.roe_report_earnings_data.items.map((item: any) => ({
				...item,
			}))
		);
	}, [roeReportData]);

			const expandedLabels = ["Reports"];
	const enhanceData = (data: any[], ancestorChain: string[] = []): any[] => {
		return data.map((item) => {
			const currentChain = [...ancestorChain, item.label];

		
			const isComplianceUnderReports =
				item.label === "Compliance" && ancestorChain.includes("Reports");

		
			const expandedROEReportOrSetupItem =
				item.label === "ROE" &&
				ancestorChain.includes("Reports") &&
				ancestorChain.includes("Compliance");

			const isExpanded =
				expandedLabels.includes(item.label) || 
				isComplianceUnderReports ||
				expandedROEReportOrSetupItem;

			return {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items
					? enhanceData(item.items, currentChain)
					: undefined,
				path: expandedROEReportOrSetupItem
					? "/reports/compliance/roe"
					: item.path ?? undefined,
			};
		});
	};


	// getting latest updated values of form fields using watch method
		const { watch } = formMethods;
		const values = watch([
			"employee",
		]);
	
		const savePdfAsFile = (xmlData: any, filename: any) => {
			const blob = new Blob([xmlData], { type: "pdf;charset=utf-8" });
			FileSaver.saveAs(blob, filename);
		};
	
		const onExportROEEarningsButtonClick = async (selectedOption: string) => {
			const [employee] = values;
	
			try {
				setIsLoading(true);
				const response = await ReportsService.exportROEEarningsReport(
					selectedOption,
					clientIdStore,
					employee,
					queryParams
				);
	
				if (response) {
					if (selectedOption === "PDF") {
						if (response.status === 400) {
							setSnackbarData({
								open: true,
								message: response.data.message
									? response.data.message
									: "Something went wrong.",
	
								severity: "error",
							});
						} else {
							const filename = `${clientName}-employee.pdf`;
							savePdfAsFile(response, filename);
						}
					} else if (selectedOption === "XLS") {
						if (response.status === 400) {
							setSnackbarData({
								open: true,
								message: response.data.message
									? response.data.message
									: "Something went wrong.",
	
								severity: "error",
							});
						} else {
							const filename = `${clientName}-employee.xlsx`;
							savePdfAsFile(response, filename);
						}
					} else {
						const fileType =
							selectedOption === "XLS"
								? "application/vnd.ms-excel"
								: "text/csv;charset=utf-8;";
						const fileExtension = selectedOption === "XLS" ? "xls" : "csv";
	
						const blob = new Blob([response], { type: fileType });
						FileSaver.saveAs(blob, `${clientName}-employee.${fileExtension}`);
					}
				} else {
					console.error("Error: No data received from the server");
				}
			} catch (err) {
	
				setIsLoading(false);
			} finally {
				setIsLoading(false);
			}
	
		};

		// ROE Hours Export Button Print
		const onExportROEHoursButtonClick = async (selectedOption: string) => {
			const [employee] = values;
	
			try {
				setIsLoading(true);
				const response = await ReportsService.exportROEHoursReport(
					selectedOption,
					clientIdStore,
					employee,
					queryParams
				);
	
				if (response) {
					if (selectedOption === "PDF") {
						if (response.status === 400) {
							setSnackbarData({
								open: true,
								message: response.data.message
									? response.data.message
									: "Something went wrong.",
	
								severity: "error",
							});
						} else {
							const filename = `${clientName}-employee.pdf`;
							savePdfAsFile(response, filename);
						}
					} else if (selectedOption === "XLS") {
						if (response.status === 400) {
							setSnackbarData({
								open: true,
								message: response.data.message
									? response.data.message
									: "Something went wrong.",
	
								severity: "error",
							});
						} else {
							const filename = `${clientName}-employee.xlsx`;
							savePdfAsFile(response, filename);
						}
					} else {
						const fileType =
							selectedOption === "XLS"
								? "application/vnd.ms-excel"
								: "text/csv;charset=utf-8;";
						const fileExtension = selectedOption === "XLS" ? "xls" : "csv";
	
						const blob = new Blob([response], { type: fileType });
						FileSaver.saveAs(blob, `${clientName}-employee.${fileExtension}`);
					}
				} else {
					console.error("Error: No data received from the server");
				}
			} catch (err) {
	
				setIsLoading(false);
			} finally {
				setIsLoading(false);
			}
	
		};
	
	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: EMPLOYEE_LIST_SCREEN_SLUG,
				accountantName: "",
				activityType: "activity_type=delete",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: true }));

		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
		dispatch(setSelectedStickyNavBar({ isSticky: false }));
	}, [drawerMenuOptions]);


	const image =
		"https://taxslips.blob.core.windows.net/taxslips-public-images-container/empty_employee_list.png";

	return (
		<>
			<div
				style={{
					margin: "15px 0",
					fontSize: "1.5rem",
					fontWeight: "bold",
					fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
				}}
			>
				{"ROE Report"}
			</div>

			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<ROEReportHeader
						setStateRunGetListAPICall={(value: boolean) => setRunGetListAPICall(value)}
						roeEmployeeDDLWithDateItems={roeEmployeeDDLWithDate && sortDDLByLabel(roeEmployeeDDLWithDate)}
					/>
				</form>
			</FormProvider>

			<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
				<BackdropCircularProgress
					open={
						isPending ||
						isSubmittingAddRemoveHeaderColumns ||
						isLoading ||
						isROEEmployeeDDLLoading

					}
					testId="roe-report"
				/>

				<ROEReportHoursDataGridWithColumnToggled
					columns={
						roeReportData?.roe_report_hours_data.headerColumns
							? roeReportData.roe_report_hours_data.headerColumns.filter(
								(item: any) => item.field !== "id"
							)
							: []
						// roeReportData?.headerColumns ?? []
					}
					completeHeaderColumnsList={
						roeReportData
							&& roeReportData
								?.roe_report_hours_data.totalHeaderColumns &&
							Object.keys(roeReportData?.roe_report_hours_data.totalHeaderColumns).length !== 0
							? roeReportData?.roe_report_hours_data.totalHeaderColumns
							: {}
						// roeReportData?.roe_report_hours_data.headerColumns ?? []
					}
					submitAddRemoveColumnsHandler={(value: any) =>
						setSubmitAddRemoveColumnsHandler(value)
					}
					onColumnReorder={(value: any) => setOnColumnReorder(value)}
					rows={
						roeReportData && updatedROEHoursDetailsListItems
							? updatedROEHoursDetailsListItems
							: []
						// roeReportData?.items ?? []
						// itams
					}
					showIndexColumn={true}
					showChecboxColumn={false}
					showSettingColumn={false}
					onSnackbarMessageData={(value: any) => setSnackbarData(value)}
					totalRecords={
						roeReportData && roeReportData.roe_report_hours_data.total
							? roeReportData.roe_report_hours_data.total
							: 0
					}
					initialPageSize={INITIAL_PAGE_SIZE}
					rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
					onRequestParamsChange={onRequestParamsChange}
					searchFieldPlaceholder={"Search by Name"}
					changeLoginForGetEmptyItemsBySearchQuery={(value: any) =>
						setzeroEmployee(value)
					}
					pageTitle={"ROE Report"}
					onExportButtonClick={onExportROEHoursButtonClick}
				/>

				<ROEReportEarningDetailDataGridWithColumnToggled
					columns={
						roeReportData?.roe_report_earnings_data.headerColumns
							? roeReportData.roe_report_earnings_data.headerColumns.filter(
								(item: any) => item.field !== "id"
							)
							: []
						// roeReportData?.headerColumns ?? []
					}
					completeHeaderColumnsList={
						roeReportData
							&& roeReportData
								?.roe_report_earnings_data.totalHeaderColumns &&
							Object.keys(roeReportData?.roe_report_earnings_data.totalHeaderColumns).length !== 0
							? roeReportData?.roe_report_earnings_data.totalHeaderColumns
							: {}
						// roeReportData?.roe_report_earnings_data.headerColumns ?? []
					}
					submitAddRemoveColumnsHandler={(value: any) =>
						setSubmitAddRemoveColumnsHandler(value)
					}
					onColumnReorder={(value: any) => setOnColumnReorder(value)}
					rows={
						roeReportData && updatedROEEarningDetailsListItems
							? updatedROEEarningDetailsListItems
							: []
						// roeReportData?.items ?? []

					}
					showIndexColumn={true}
					showChecboxColumn={false}
					showSettingColumn={false}
					onSnackbarMessageData={(value: any) => setSnackbarData(value)}
					totalRecords={
						roeReportData && roeReportData.roe_report_earnings_data.total
							? roeReportData.roe_report_earnings_data.total
							: 0
					}
					initialPageSize={INITIAL_PAGE_SIZE}
					rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
					onRequestParamsChange={onRequestParamsChange}
					searchFieldPlaceholder={"Search by Name"}
					changeLoginForGetEmptyItemsBySearchQuery={(value: any) =>
						setzeroEmployee(value)
					}
					pageTitle={"ROE Report"}
					onExportButtonClick={onExportROEEarningsButtonClick}
				/>
				{roeReportData ? (
					<AppBar
						position="sticky"
						color="default"
						elevation={1}
						sx={{
							backgroundColor: "#F7F7F7",

							boxShadow: "none",
							alignItems: "center",
							zIndex: 0,
							position: "sticky",
							bottom: 0,
						}}
					></AppBar>
				) : (
					<></>
				)}
			</Grid>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={`snackbar`}
				open={snackbarData.open}
			/>
		</>
	);
};
